/*
 * @Author: Chao Yang
 * @Date: 2019-09-29 08:18:22
 * @LastEditor: Chao Yang
 * @LastEditTime: 2022-08-10 15:11:17
 */
import { Injectable } from '@angular/core';
import { SHA256, enc } from 'crypto-js';

import { IInfo } from '../../model/info';
import {
  HeaderOption,
  HeaderMenuItem,
  FooterOption,
  MenuOption,
  MenuItem,
} from 'section';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InfoService {
  public info: IInfo;

  private infoKey = 'ad-ip-info';

  constructor() {
    if (
      localStorage.getItem(this.infoKey) === undefined ||
      localStorage.getItem(this.infoKey) === null
    ) {
      window.location.href =
        '/transferstation/investorportal?returnUrl=' +
        encodeURIComponent(window.location.href);
    } else {
      this.info = JSON.parse(localStorage.getItem(this.infoKey));
      window['corproUserHash'] = SHA256(
        this.info.user.profile.userName
      ).toString(enc.Hex);
      window['corproUserIsManager'] = this.info.user.isManager;
      window['isUserInternal'] = this.info.user.internal;
      environment.update(this.info.features);
    }
  }

  get maxDownloadCount() {
    return this.info.site.setting.maxDownloadCount || 20;
  }

  getDSFEntry(): string {
    return this.info.site.dsfEntry;
  }

  getBranding(): string {
    return this.info.branding.name === '' ? 'default' : this.info.branding.name;
  }

  getLogoPath() {
    return (
      this.info.branding.logoPath ||
      'assets/images/lib/section/header/header_logo.png'
    );
  }

  getFooterHtml() {

    return this.info.branding.footer
      ? decodeURIComponent(window.escape(window.atob(this.info.branding.footer)))
      : '';
  }

  getFooterOptions(): FooterOption {
    const footerOption = new FooterOption();
    footerOption.branding = this.info.branding.name;
    let region = this.info.site.region;
    if (region && region.toLocaleLowerCase() === 'emea') {
      footerOption.email = 'CorProGlobalSupport@alterdomus.com';
      footerOption.phone = '+352 48 18 28 6060';
      footerOption.copyRight = '[[year]] Alter Domus';
      footerOption.opertionTime = '6am - 11pm CET';
    }
    footerOption.footerHtml = this.getFooterHtml();
    return footerOption;
  }

  getHeaderOptions(): HeaderOption {
    const headerOption = new HeaderOption();
    headerOption.branding = this.info.branding.name;
    headerOption.logo.link = this.info.site.link;
    headerOption.user.name = this.info.user.profile.name;
    headerOption.logoHeight = this.info.branding.logoHeight;
    headerOption.showAllPortfolioLink = this.info.showAllPortfolioLink;
    this.info.user.menus.forEach((menu) => {
      const headerMenu = new HeaderMenuItem();
      headerMenu.text = menu.text;
      headerMenu.link = menu.link;
      headerMenu.target = menu.target;
      headerOption.user.menus.push(headerMenu);
    });
    return headerOption;
  }

  getCorProEmailLink(): string {
    return 'mailto:CorProSupport@alterDomus.com';
  }

  getDateFormat(): string {
    return this.info.user.profile.format.shortDatePattern;
  }

  getDateExcelFormat(): string {
    return this.getDateFormat() + '* ;;;@';
  }

  getNumberDigits() {
    return `1.${this.info.user.profile.format.numberDecimalDigits}-${this.info.user.profile.format.numberDecimalDigits}`;
  }

  getNumberExcelFormatObj(numberDecimalDigits?: number): {
    eFormat: string;
    eFormat_WithX: string;
    eFormat_NB: string;
    eFormat_Left: string;
  } {
    let digits =
      numberDecimalDigits ??
      this.info.user.profile.format.numberDecimalDigits,
      number_precision = '';
    for (var i = digits; i > 0; number_precision += '0', i--);
    if (number_precision) {
      number_precision = '.' + number_precision;
    }
    return {
      eFormat:
        '_ * #,##0' +
        number_precision +
        '_ ;_ * (#,##0' +
        number_precision +
        ') ;_ * #,##0' +
        number_precision +
        '_ ;_ * @ ', //Number excel cell format
      eFormat_WithX:
        '_ * #,##0' +
        number_precision +
        '"x"_ ;_ * (#,##0' +
        number_precision +
        ')"x" ;_ * "0.0x" ;_ * @ ', //Number excel cell format && With 'X'
      eFormat_NB:
        '#,###' +
        number_precision +
        '#;-#,###' +
        number_precision +
        '#;#,##0' +
        number_precision +
        '#;_ * @ ', //Number excel cell format && No Brackets
      eFormat_Left:
        '#,##0' +
        number_precision +
        '_ * ;(#,##0' +
        number_precision +
        ') * ;#,##0' +
        number_precision +
        '_ ;@ * ', //Number excel cell format && align
    };
  }

  getPercentageDigits() {
    return `1.${this.info.user.profile.format.percentDecimalDigits}-${this.info.user.profile.format.percentDecimalDigits}`;
  }

  getPercentageExcelFormatObj(percentDecimalDigits?: number): {
    eFormat: string;
    eFormat_Left: string;
  } {
    let digits =
      percentDecimalDigits ??
      this.info.user.profile.format.percentDecimalDigits,
      perentage_precision = '';
    for (var i = digits; i > 0; perentage_precision += '0', i--);
    if (perentage_precision) {
      perentage_precision = '.' + perentage_precision;
    }
    return {
      eFormat:
        '0' +
        perentage_precision +
        '%;(0' +
        perentage_precision +
        '%);0' +
        perentage_precision +
        '%;_ * @ ', //Perentage excel cell format
      eFormat_Left:
        '0' +
        perentage_precision +
        '% * ;(0' +
        perentage_precision +
        '%) * ;0' +
        perentage_precision +
        '% * ;@ * ', //Perentage excel cell format &&
    };
  }

  ///only the percent decimal digit
  getPercentageDecimalDigits() {
    return this.info.user.profile.format.percentDecimalDigits;
  }

  ///only the number decimal digit
  getNumberDecimalDigits() {
    return this.info.user.profile.format.numberDecimalDigits;
  }

  getLastTime(): number {
    return this.info.user.last;
  }

  getMainMenuOptions(): MenuOption {
    const menuOption = new MenuOption();
    menuOption.branding = this.info.branding.name;
    this.info.site.setting.mainMenuItems.forEach((menu) => {
      const mainMenu = new MenuItem();
      mainMenu.text = menu.text;
      mainMenu.link = menu.link;
      mainMenu.iconClass = menu.iconClass;
      if (menu.setting) {
        mainMenu.isExternal = menu.setting.isExternal;
      } else {
        mainMenu.isExternal = false;
      }
      menuOption.menus.push(mainMenu);
    });
    return menuOption;
  }

  hasOnboardingMenu(): boolean {
    return (
      this.getMainMenuOptions().menus.findIndex(
        (it) => it.text === 'Onboarding'
      ) >= 0
    );
  }

  getSiteTimeZone(): string {
    return this.info.site.timeZone;
  }

  getUserId(): number {
    return this.info.user.id;
  }

  getUserName() {
    return this.info.user.profile.name;
  }
  isManager(): boolean {
    return this.info.user.isManager;
  }

  isInternal(): boolean {
    return this.info.user.internal;
  }

  getGlobalCurrency(): string {
    return this.info.user.globalCurrency;
  }

  setGlobalCurrency(currency: string) {
    this.info.user.globalCurrency = currency;
    localStorage[this.infoKey] = JSON.stringify(this.info);
  }

  getDisclaimers() {
    return this.info.user.disclaimerCodes
      ? this.info.user.disclaimerCodes.filter((c) => c !== '')
      : [];
  }

  clearDisclaimers() {
    this.info.user.disclaimerCodes = [];
    localStorage[this.infoKey] = JSON.stringify(this.info);
  }
  getCurrentUser(): {
    id: number;
    userName: string;
    firstName: string;
    lastName: string;
    fullName: string;
    isInShadow: boolean;
  } {
    return this.info.user.dsfUser;
  }

  getNumberDigitsByNumber(numberDecimalDigits: number) {
    return `1.${numberDecimalDigits}-${numberDecimalDigits}`;
  }

  getPercentageDigitsByNumber(percentDecimalDigits: number) {
    return `1.${percentDecimalDigits}-${percentDecimalDigits}`;
  }

  shadowSession() {
    return this.info.shadowSession;
  }
}
